import { useAbility } from '@casl/react'
import React, { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { CustomerTypeEnum, MeContext, UserPermissionFeature } from '../../api'
import { AbilityContext } from '../../api/components/Can'
import { isCloakedUser } from '../../api/utils/cloak'
import { isNonAdminSupplier as isNonAdminSupplierPrincipal } from '../../api/utils/nonAdminSupplier'
import { useRebateOptimizerAccess } from '../../common/hooks/useRebateOptimizerAccess'
import { CategoriesSection } from './Categories/Categories'
import { FeaturedContentCarousel } from './components/FeaturedContent'
import { ContractsSection } from './Contracts/Contracts'
import { OpportunitiesSection } from './Opportunities/Opportunities'
import { PlaneIssues } from './PlaneIssues/PlaneIssues'

export const Home: React.FC<{}> = () => {
  const { data } = useContext(MeContext)
  const ability = useAbility(AbilityContext)
  const { basketsLite, hasRebateOptimizer } = useRebateOptimizerAccess()

  // Rebate-Optimizer users get the lite Market Basket Management page instead of the home Market
  // Baskets carousel, and have no RFx. Hide both here unless the user also has that specific feature
  // (a mixed user with Baskets / Opportunities still sees them) — per-surface model (#7206).
  const showMarketBaskets = !basketsLite
  const showRfx = !hasRebateOptimizer || ability.can('access', UserPermissionFeature.Opportunities)

  // #7701: a supplier's Home hides the four hospital sections — they all link into route families that
  // are now closed to them, so leaving them would recreate the nav/route disagreement that bug exists to
  // close. #7701 also claimed the supplier still gets featured content; that half was never true — a real
  // supplier's Home shows no hospital section and no featured-content request is issued (#7843 §15 F1).
  // The carousel is owned by the `isRealSupplier` block below, not by this predicate. The `!isAdmin` term
  // keeps an admin's (supplier-typed or not) single pre-redirect frame byte-identical: the redirect below
  // is an effect, so that frame paints today.
  const isNonAdminSupplier = isNonAdminSupplierPrincipal(data?.me)

  // #7843: `userHomepageFeaturedContents` carries no `@AllowSupplier()`, so for a REAL supplier the
  // server refuses it structurally (403 FORBIDDEN, silent by design) and the carousel can only ever be
  // empty. Gate it on the population that is proven to fail, which is NOT `isNonAdminSupplier` above:
  // an admin cloaked into a supplier customer reports `isAdmin: false` yet is admitted by the guard via
  // `isOverride`, so that term would stop a request that succeeds. The cloak term is what makes this
  // exact; the four hospital sections keep the wider `isNonAdminSupplier` rule #7701 shipped.
  const isRealSupplier =
    data?.me.user.customer.type === CustomerTypeEnum.Supplier &&
    data?.me.isAdmin !== true &&
    !isCloakedUser(data?.me.user)

  const navigate = useNavigate()
  useEffect(() => {
    if (data?.me?.isAdmin) {
      navigate('/manage/customer')
    }
  })

  return (
    <Wrapper>
      <Helmet>
        <title>Curvo</title>
      </Helmet>
      {!isRealSupplier ? <FeaturedContentCarousel /> : null}
      {!isNonAdminSupplier && ability.can('access', UserPermissionFeature.ProjectAperture) ? <PlaneIssues /> : null}
      {!isNonAdminSupplier && showMarketBaskets ? <CategoriesSection /> : null}
      {!isNonAdminSupplier && showRfx ? <OpportunitiesSection /> : null}
      {!isNonAdminSupplier ? <ContractsSection /> : null}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
  padding: 24px 32px;
`
